import { useEffect, useState } from "react";
import { clearImpersonationStorage } from "../utils/impersonation";
import { resetLogRocketSession } from "../utils/logRocket";

const isElectron = () =>
  typeof window !== "undefined" && window.desktopRuntime?.isElectron === true;

const readProfile = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem("userData") || "{}");
    return stored?.data || stored || {};
  } catch {
    return {};
  }
};

const getExpiryDate = (profile) =>
  profile?.validupto ||
  profile?.valid_upto ||
  profile?.validTill ||
  profile?.valid_till ||
  profile?.expiry_date ||
  null;

const parseExpiryAtEndOfDay = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59, 999);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const DesktopSubscriptionGuard = () => {
  const [expired, setExpired] = useState(false);
  const [validityLabel, setValidityLabel] = useState("");

  const handleLogout = () => {
    if (!window.confirm("Are you sure you want to logout?")) return;

    resetLogRocketSession();
    clearImpersonationStorage();
    window.localStorage.removeItem("userData");
    window.localStorage.removeItem("checkvalidData");
    setExpired(false);
    window.location.hash = "#/login";
  };

  useEffect(() => {
    if (!isElectron()) return undefined;

    const checkValidity = () => {
      const expiry = parseExpiryAtEndOfDay(getExpiryDate(readProfile()));
      setExpired(Boolean(expiry && Date.now() > expiry.getTime()));
      setValidityLabel(
        expiry
          ? expiry.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : ""
      );
    };

    checkValidity();
    const timer = window.setInterval(checkValidity, 60 * 1000);
    window.addEventListener("storage", checkValidity);
    window.addEventListener("focus", checkValidity);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("storage", checkValidity);
      window.removeEventListener("focus", checkValidity);
    };
  }, []);

  if (!isElectron() || !expired) return null;

  return (
    <div style={styles.backdrop} role="alertdialog" aria-modal="true" aria-label="Subscription expired">
      <div style={styles.card}>
        <div style={styles.icon}>!</div>
        <h1 style={styles.title}>Subscription expired</h1>
        <p style={styles.message}>
          Your desktop app validity {validityLabel ? `ended on ${validityLabel}` : "has ended"}.
          Please renew your subscription to continue using BillsArthi.
        </p>
        <p style={styles.help}>Contact BillsArthi support to renew your plan.</p>
        <button type="button" style={styles.logoutButton} onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 2147483647,
    display: "grid",
    placeItems: "center",
    padding: 24,
    background: "rgba(8, 20, 36, 0.92)",
  },
  card: {
    width: "min(440px, 100%)",
    padding: "34px 28px",
    borderRadius: 18,
    textAlign: "center",
    color: "#10233b",
    background: "#fff",
    boxShadow: "0 24px 70px rgba(0,0,0,.4)",
  },
  icon: {
    width: 54,
    height: 54,
    margin: "0 auto 16px",
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    background: "#dc2626",
    fontWeight: 900,
    fontSize: 30,
  },
  title: { margin: "0 0 12px", fontSize: 24 },
  message: { margin: 0, color: "#475569", lineHeight: 1.55, fontSize: 15 },
  help: { margin: "16px 0 0", color: "var(--app-primary)", fontWeight: 700, fontSize: 14 },
  logoutButton: {
    minWidth: 132,
    minHeight: 44,
    marginTop: 22,
    padding: "10px 20px",
    border: "1px solid #dc2626",
    borderRadius: 10,
    color: "#b91c1c",
    background: "#fff",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
  },
};

export default DesktopSubscriptionGuard;
